import React, { useState } from 'react';
import { 
  Typography, 
  Stack, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Policy as PolicyIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import SectionPaper from '../../../../../common/components/styled-ui-elements/SectionPaper';

const PolicyLinksField: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentCase } = useAppSelector(state => state.caseManage);
  const [selectedPolicy, setSelectedPolicy] = useState<{ id: string; name: string; content: string } | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Mock policy data - in real implementation, this would come from API
  const handlePolicyClick = (policyId: string, policyName: string) => {
    // This would typically fetch policy details from an API
    setSelectedPolicy({
      id: policyId,
      name: policyName,
      content: `Policy details for ${policyName} would be displayed here. This would include medical necessity criteria, coverage requirements, and other relevant policy information.`
    });
    setDialogOpen(true);
  };

  const handlePolicyReviewToggle = (policyId: string, reviewed: boolean) => {
    // This would update the policy link reviewed status
    // For now, we'll just log it since we need to implement the proper Redux action
    console.log(`Policy ${policyId} reviewed status changed to: ${reviewed}`);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedPolicy(null);
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1e293b' }}>
          <PolicyIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: '1.2rem' }} />
          Policy Links
        </Typography>
        
        {currentCase?.policyLinks && currentCase.policyLinks.length > 0 ? (
          <SectionPaper sx={{ backgroundColor: '#ffffff' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please review the following policies to ensure compliance with medical guidelines:
            </Typography>
            
            <Stack spacing={1}>
              {currentCase.policyLinks.map((link, index) => (
                <Stack key={link.policyId || index} direction="row" alignItems="center" justifyContent="space-between">
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={() => handlePolicyClick(link.policyId, link.policyName)}
                    sx={{ 
                      textAlign: 'left',
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    {link.policyName || 'Unnamed Policy'}
                  </Button>
                  
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={link.reviewed || false}
                        onChange={(e) => handlePolicyReviewToggle(link.policyId, e.target.checked)}
                        size="small"
                        color="primary"
                      />
                    }
                    label="Reviewed"
                    sx={{ fontSize: '0.875rem' }}
                  />
                </Stack>
              ))}
            </Stack>
          </SectionPaper>
        ) : (
          <SectionPaper>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              No policy links available for this case
            </Typography>
          </SectionPaper>
        )}
      </Stack>

      {/* Policy Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { minHeight: '400px' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{selectedPolicy?.name}</Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
            {selectedPolicy?.content}
          </Typography>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PolicyLinksField;
